import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  MessagesSquare,
  Pencil,
  Trash2,
  Bot,
  Eye,
  Users,
  Activity,
  TrendingUp,
  Clock,
  Sparkles,
} from "lucide-react";
import { TemplateGallery } from "@/components/portal/TemplateGallery";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { getDefaultAIChatConfig } from "@/hooks/useAIChatModeConfig";
import { DEFAULT_UNIFIED_SETTINGS } from "@/types/builder";

interface Chatbot {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
  created_at: string;
  updated_at?: string | null;
  user_id: string;
  avatar_url: string | null;
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 30) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}

export function ChatbotList() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);
  const [isTemplateGalleryOpen, setIsTemplateGalleryOpen] = useState(false);
  const [filterActive, setFilterActive] = useState(false);  // filter to show only published
  const [showLeads, setShowLeads] = useState(false);        // show leads modal
  const [leads, setLeads] = useState<any[]>([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [selectedLead, setSelectedLead] = useState<any>(null);

  // ── Self-contained data fetching ──────────────────────────
  //
  // Why not useQuery or a simple useEffect([userId])?
  //
  // AuthContext populates `user` asynchronously via supabase.auth.getSession()
  // which resolves AFTER the first React render. ChatbotList mounts inside
  // the skeleton branch, its useEffect runs with userId=null, and React
  // does NOT re-trigger the effect when AuthContext later updates because
  // the component is already mounted with a stale closure over null.
  //
  // React Query v5 has the same problem: `enabled: !!userId` evaluates
  // false on mount and the query enters a "pending-disabled" state that
  // never transitions to "fetching" when enabled flips.
  //
  // The nuclear fix: read the Supabase session directly via
  // supabase.auth.getSession() inside the effect. This is guaranteed to
  // return the current session regardless of React context timing. Once
  // we have the user ID, we fetch chatbots. One await chain, zero
  // dependency on React context propagation timing.
  const [chatbots, setChatbots] = useState<Chatbot[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [userId, setUserId] = useState<string | null>(null);

  const fetchChatbotsForUser = useCallback(async (uid: string) => {
    setUserId(uid);
    setIsLoading(true);
    try {
      // Use raw REST helper — supabase JS client hangs on production domain
      const { supabaseGet } = await import('@/utils/supabaseRest');
      const data = await supabaseGet<Chatbot[]>(
        `chatbots?select=id,name,description,published,updated_at,created_at&user_id=eq.${uid}&order=created_at.desc`
      );
      setChatbots(data);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // BULLETPROOF approach: poll localStorage for the Supabase session token.
  //
  // Every other approach failed because of "Multiple GoTrueClient instances"
  // — the app has 2 Supabase clients (main + service) with different storage
  // keys. supabase.auth.getSession() and onAuthStateChange ONLY fire on the
  // client instance that performed the login. If ChatbotList's imported
  // `supabase` singleton happens to be initialized before the login client
  // finishes writing the session, it never sees the SIGNED_IN event.
  //
  // Reading localStorage directly is immune to all of this: the token is
  // written once by whichever client performed the login, and ANY code on
  // the same origin can read it. A 500ms poll is imperceptible to the user
  // and guarantees we catch the session within one render cycle.
  useEffect(() => {
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval>;

    function tryLoadFromStorage() {
      try {
        const raw = localStorage.getItem('supabase.auth.token');
        if (!raw) return false;
        const parsed = JSON.parse(raw);
        const uid = parsed?.user?.id;
        if (uid && typeof uid === 'string') {
          if (!cancelled) fetchChatbotsForUser(uid);
          return true;
        }
      } catch { /* malformed token */ }
      return false;
    }

    // Immediate check (return visits with cached session)
    if (!tryLoadFromStorage()) {
      // Not found yet — poll until it appears (fresh login in progress)
      intervalId = setInterval(() => {
        if (tryLoadFromStorage() || cancelled) {
          clearInterval(intervalId);
        }
      }, 500);

      // Safety: stop polling after 30s to prevent infinite loop
      setTimeout(() => {
        clearInterval(intervalId);
        if (!cancelled && chatbots === null) setIsLoading(false);
      }, 30000);
    }

    return () => { cancelled = true; clearInterval(intervalId); };
  }, [fetchChatbotsForUser]);

  const refetch = useCallback(() => {
    if (userId) fetchChatbotsForUser(userId);
  }, [userId, fetchChatbotsForUser]);

  // Stats — raw REST to avoid supabase client hang
  const [stats, setStats] = useState({ conversations: 0, active: 0, leads: 0 });
  useEffect(() => {
    if (!chatbots || chatbots.length === 0) return;
    let cancelled = false;
    const ids = chatbots.map((c) => c.id).join(',');

    (async () => {
      const { supabaseCount } = await import('@/utils/supabaseRest');
      const [convCount, leadCount] = await Promise.all([
        supabaseCount(`conversations?chatbot_id=in.(${ids})`),
        supabaseCount(`leads?chatbot_id=in.(${ids})`),
      ]);
      if (!cancelled) {
        setStats({
          conversations: convCount,
          active: chatbots.filter((c) => (c as any).published).length,
          leads: leadCount,
        });
      }
    })();
    return () => { cancelled = true; };
  }, [chatbots]);

  const deleteChatbot = useMutation({
    mutationFn: async (chatbotId: string) => {
      const { supabaseDelete } = await import('@/utils/supabaseRest');
      await supabaseDelete(`chatbots?id=eq.${chatbotId}&user_id=eq.${userId}`);
    },
    onSuccess: () => {
      refetch();
      toast({ title: "Success", description: "Chatbot deleted successfully" });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to delete chatbot",
      });
    },
  });

  // Load leads with obfuscated PII — we own the data, users just monetize
  const loadLeads = async () => {
    if (!chatbots || chatbots.length === 0) return;
    setLeadsLoading(true);
    setShowLeads(true);
    try {
      const { supabaseGet } = await import('@/utils/supabaseRest');
      const ids = chatbots.map(c => c.id).join(',');
      const data = await supabaseGet<any[]>(
        `leads?select=id,name,email,phone,status,source,vertical,payout,data,page_url,referrer,utm_source,utm_medium,utm_campaign,ip_address,user_agent,created_at&chatbot_id=in.(${ids})&order=created_at.desc&limit=50`
      );
      setLeads(data || []);
    } catch { setLeads([]); }
    finally { setLeadsLoading(false); }
  };

  // Obfuscate a string: show first 2 chars + *** + last 2 chars
  const obfuscate = (val: string | null): string => {
    if (!val || val.length < 5) return '•••••';
    return val.substring(0, 2) + '•••' + val.substring(val.length - 2);
  };

  // Filtered chatbots based on active filter
  const displayedChatbots = filterActive
    ? (chatbots || []).filter(c => (c as any).published)
    : (chatbots || []);

  const handleCreateChatbot = async () => {
    if (!userId) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "You must be logged in to create a chatbot",
      });
      return;
    }

    const chatbotName = window.prompt('Name your chatbot:', 'My Chatbot');
    if (!chatbotName) return; // User cancelled

    setIsCreating(true);
    try {
      const { supabasePost } = await import('@/utils/supabaseRest');

      const chatbotRows = await supabasePost<Array<{id: string}>>('chatbots', {
        name: chatbotName.trim(),
        description: '',
        user_id: userId,
      });
      const data = Array.isArray(chatbotRows) ? chatbotRows[0] : chatbotRows;
      if (!data?.id) throw new Error('Failed to create chatbot');

      await supabasePost('themes', {
        chatbot_id: data.id,
        name: "Default Theme",
        primarycolor: "#2563EB",
        backgroundcolor: "#FFFFFF",
        userbubblecolor: "#3B82F6",
        botbubblecolor: "#F3F4F6",
        usertextcolor: "#FFFFFF",
        bottextcolor: "#1F2937",
        headercolor: "#3B82F6",
        headertextcolor: "#FFFFFF",
        borderradius: "8px",
        fontfamily: "Inter",
        buttoncolor: "#3B82F6",
        buttontextcolor: "#FFFFFF",
        position: "right",
        buttontext: "Chat with us",
      });

      await refetch();
      navigate(`/portal/flow/${data.id}`);
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: err.message,
      });
    } finally {
      setIsCreating(false);
    }
  };

  // Loading skeleton — only show while we genuinely don't have data yet.
  //
  // CRITICAL: React Query v5 changed `isLoading` semantics. With
  // `enabled: !!userId` and userId starting as null, the query is
  // permanently in the "pending" state and `isLoading` reports `true`
  // forever — even after userId is populated and the query starts
  // returning data. Symptom: skeleton renders forever.
  //
  // The fix: gate the skeleton on absence of data, not on `isLoading`.
  // Once `chatbots` is defined (even an empty array), render the real UI.
  // Show skeleton ONLY while chatbots haven't loaded yet.
  // Never gate on !userId — that creates an infinite skeleton loop because
  // the component returns before the useEffect (which depends on userId)
  // can fire. Instead, let the component render, let the effect fire when
  // userId arrives, and only show skeleton when data is truly absent.
  if (chatbots === null) {
    return (
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <Skeleton className="h-9 w-48 bg-slate-800" />
          <Skeleton className="h-10 w-40 bg-slate-800" />
        </div>
        <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-28 bg-slate-800" />
          ))}
        </div>
        <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-48 bg-slate-800" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-white">
        <p className="text-red-400">Failed to load chatbots</p>
        <Button
          variant="outline"
          onClick={() => refetch()}
          className="mt-4 border-slate-700 bg-slate-900 text-white hover:bg-slate-800 hover:text-emerald-400"
        >
          Retry
        </Button>
      </div>
    );
  }

  const hasChatbots = chatbots && chatbots.length > 0;

  return (
    <>
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">My Chatbots</h1>
          <p className="text-slate-400 mt-1">
            Build, manage and monitor your AI chatbots
          </p>
        </div>
        {hasChatbots && (
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setIsTemplateGalleryOpen(true)}
              className="border-emerald-500/40 bg-slate-900 text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-300 font-semibold"
            >
              <Sparkles className="mr-2 h-4 w-4" />
              Use a Template
            </Button>
            <Button
              onClick={handleCreateChatbot}
              disabled={isCreating}
              className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-semibold"
            >
              <Plus className="mr-2 h-4 w-4" />
              {isCreating ? "Creating..." : "Create New Chatbot"}
            </Button>
          </div>
        )}
      </div>

      {/* Quick Stats */}
      {hasChatbots && (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
          <Card className="bg-slate-900 border-slate-800 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Total Conversations</p>
                <p className="text-3xl font-bold text-white mt-1">
                  {stats?.conversations ?? 0}
                </p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <MessagesSquare className="h-6 w-6 text-emerald-400" />
              </div>
            </div>
          </Card>
          <Card
            className="bg-slate-900 border-slate-800 p-6 cursor-pointer hover:border-emerald-500/40 transition-colors"
            onClick={() => setFilterActive(!filterActive)}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Active Chatbots</p>
                <p className="text-3xl font-bold text-white mt-1">
                  {stats?.active ?? 0}
                </p>
                <p className="text-xs text-emerald-400 mt-1">
                  {filterActive ? '← Show all' : 'Click to filter active'}
                </p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <Activity className="h-6 w-6 text-emerald-400" />
              </div>
            </div>
          </Card>
          <Card
            className="bg-slate-900 border-slate-800 p-6 cursor-pointer hover:border-emerald-500/40 transition-colors"
            onClick={loadLeads}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Leads Captured</p>
                <p className="text-3xl font-bold text-white mt-1">
                  {stats?.leads ?? 0}
                </p>
                <p className="text-xs text-emerald-400 mt-1">Click to view leads</p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-emerald-400" />
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Empty state */}
      {!hasChatbots ? (
        <Card className="bg-slate-900 border-slate-800 border-dashed p-12 text-center">
          <div className="mx-auto h-16 w-16 rounded-full bg-emerald-500/10 flex items-center justify-center">
            <Bot className="h-8 w-8 text-emerald-400" />
          </div>
          <h3 className="mt-6 text-2xl font-bold text-white">
            Create your first chatbot
          </h3>
          <p className="mt-2 text-slate-400 max-w-md mx-auto">
            Get started with IntentDrive.ai and build a powerful AI assistant for
            your business in minutes.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={handleCreateChatbot}
              disabled={isCreating}
              size="lg"
              className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-semibold"
            >
              <Plus className="mr-2 h-5 w-5" />
              {isCreating ? "Creating..." : "Create New Chatbot"}
            </Button>
            <Button
              onClick={() => setIsTemplateGalleryOpen(true)}
              size="lg"
              variant="outline"
              className="border-emerald-500/40 bg-slate-900 text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-300 font-semibold"
            >
              <Sparkles className="mr-2 h-5 w-5" />
              Use a Template
            </Button>
          </div>
        </Card>
      ) : (
        <>
          {/* Chatbot Grid */}
          <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {/* Create New Card */}
            <button
              onClick={handleCreateChatbot}
              disabled={isCreating}
              className="group relative rounded-lg border-2 border-dashed border-slate-800 bg-slate-900/50 p-6 text-left transition-all hover:border-emerald-500/50 hover:bg-slate-900 min-h-[200px] flex flex-col items-center justify-center disabled:opacity-50"
            >
              <div className="h-14 w-14 rounded-full bg-emerald-500/10 flex items-center justify-center group-hover:bg-emerald-500/20 transition-colors">
                <Plus className="h-7 w-7 text-emerald-400" />
              </div>
              <h3 className="mt-4 font-semibold text-white">
                {isCreating ? "Creating..." : "Create New Chatbot"}
              </h3>
              <p className="mt-1 text-sm text-slate-400 text-center">
                Start building a new AI assistant
              </p>
            </button>

            {displayedChatbots.map((chatbot) => (
              <Card
                key={chatbot.id}
                className="group bg-slate-900 border-slate-800 hover:border-emerald-500/50 transition-colors p-6 flex flex-col"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="h-12 w-12 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                    <Bot className="h-6 w-6 text-emerald-400" />
                  </div>
                  <span
                    className={
                      chatbot.published
                        ? "inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-400 border border-emerald-500/20"
                        : "inline-flex items-center gap-1.5 rounded-full bg-slate-800 px-2.5 py-1 text-xs font-medium text-slate-400 border border-slate-700"
                    }
                  >
                    <span
                      className={
                        chatbot.published
                          ? "h-1.5 w-1.5 rounded-full bg-emerald-400"
                          : "h-1.5 w-1.5 rounded-full bg-slate-500"
                      }
                    />
                    {chatbot.published ? "Active" : "Draft"}
                  </span>
                </div>

                <h3 className="font-semibold text-white text-lg line-clamp-1">
                  {chatbot.name}
                </h3>
                <p className="text-sm text-slate-400 mt-1 line-clamp-2 min-h-[2.5rem]">
                  {chatbot.description || "No description"}
                </p>

                <div className="flex items-center gap-4 mt-4 text-xs text-slate-500">
                  <div className="flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" />
                    <span>
                      {formatRelativeTime(chatbot.updated_at || chatbot.created_at)}
                    </span>
                  </div>
                </div>

                <div className="flex gap-2 mt-6 pt-4 border-t border-slate-800">
                  <Button
                    size="sm"
                    onClick={() => window.open(`/try?chatbot=${chatbot.id}`, '_blank')}
                    className="flex-1 bg-emerald-500/10 hover:bg-emerald-500 hover:text-slate-950 text-emerald-400 border border-emerald-500/30"
                  >
                    <Eye className="h-3.5 w-3.5 mr-1.5" />
                    Preview
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => navigate(`/portal/flow/${chatbot.id}`)}
                    className="flex-1 bg-slate-800 hover:bg-emerald-500 hover:text-slate-950 text-white border border-slate-700"
                  >
                    <Pencil className="h-3.5 w-3.5 mr-1.5" />
                    Edit
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-slate-700 bg-slate-900 text-slate-400 hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/50"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-slate-900 border-slate-800 text-white">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">
                          Delete Chatbot
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">
                          Are you sure you want to delete "{chatbot.name}"? This
                          action cannot be undone. All associated data including
                          conversations, themes, and settings will be permanently
                          removed.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700 hover:text-white">
                          Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => deleteChatbot.mutate(chatbot.id)}
                          className="bg-red-600 hover:bg-red-700 text-white"
                          disabled={deleteChatbot.isPending}
                        >
                          {deleteChatbot.isPending ? "Deleting..." : "Delete"}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </Card>
            ))}
          </div>

          {/* Recent Activity Feed */}
          <Card className="bg-slate-900 border-slate-800 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Activity className="h-5 w-5 text-emerald-400" />
              <h2 className="text-lg font-semibold text-white">Recent Activity</h2>
            </div>
            <div className="space-y-3">
              {chatbots.slice(0, 5).map((chatbot) => (
                <div
                  key={chatbot.id}
                  className="flex items-center justify-between py-2 border-b border-slate-800 last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                      <Bot className="h-4 w-4 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-sm text-white font-medium">
                        {chatbot.name}
                      </p>
                      <p className="text-xs text-slate-500">
                        {chatbot.published ? "Active" : "Draft"} ·{" "}
                        {formatRelativeTime(
                          chatbot.updated_at || chatbot.created_at
                        )}
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => navigate(`/portal/flow/${chatbot.id}`)}
                    className="text-slate-400 hover:text-emerald-400 hover:bg-slate-800"
                  >
                    View
                  </Button>
                </div>
              ))}
            </div>
          </Card>
        </>
      )}
    </div>
    <TemplateGallery
      open={isTemplateGalleryOpen}
      onOpenChange={setIsTemplateGalleryOpen}
    />

    {/* Leads Modal — obfuscated PII, we own the data */}
    {showLeads && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setShowLeads(false)}>
        <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
            <div>
              <h2 className="text-lg font-bold text-white">Leads Captured</h2>
              <p className="text-xs text-slate-500">All leads captured by your conversion engines</p>
            </div>
            <button onClick={() => setShowLeads(false)} className="text-slate-400 hover:text-white text-xl">&times;</button>
          </div>
          <div className="overflow-y-auto max-h-[60vh]">
            {leadsLoading ? (
              <div className="p-8 text-center text-slate-500">Loading leads...</div>
            ) : leads.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No leads yet</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-800/50 text-slate-400 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-2">Date</th>
                    <th className="text-left px-4 py-2">Name</th>
                    <th className="text-left px-4 py-2">Email</th>
                    <th className="text-left px-4 py-2">Phone</th>
                    <th className="text-left px-4 py-2">Status</th>
                    <th className="text-left px-4 py-2">Source</th>
                    <th className="text-right px-4 py-2">Payout</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead: any) => (
                    <React.Fragment key={lead.id}>
                      <tr
                        className="border-t border-slate-800 hover:bg-slate-800/30 cursor-pointer"
                        onClick={() => setSelectedLead(selectedLead?.id === lead.id ? null : lead)}
                      >
                        <td className="px-4 py-2 text-slate-400 text-xs">{new Date(lead.created_at).toLocaleDateString()}</td>
                        <td className="px-4 py-2 text-white">{lead.name || '—'}</td>
                        <td className="px-4 py-2 text-slate-400">{lead.email || '—'}</td>
                        <td className="px-4 py-2 text-slate-400">{lead.phone || '—'}</td>
                        <td className="px-4 py-2">
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            lead.status === 'converted' ? 'bg-emerald-500/10 text-emerald-400'
                            : lead.status === 'contacted' ? 'bg-blue-500/10 text-blue-400'
                            : lead.status === 'qualified' ? 'bg-violet-500/10 text-violet-400'
                            : 'bg-slate-800 text-slate-400'
                          }`}>{lead.status || 'new'}</span>
                        </td>
                        <td className="px-4 py-2 text-xs text-slate-500">{lead.source || 'flow'}</td>
                        <td className="px-4 py-2 text-right font-mono text-emerald-400">{lead.payout ? `$${Number(lead.payout).toFixed(2)}` : '—'}</td>
                      </tr>
                      {selectedLead?.id === lead.id && (
                        <tr>
                          <td colSpan={7} className="bg-slate-800/30 px-4 py-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                              <div className="space-y-2">
                                <h4 className="text-emerald-400 font-semibold text-xs uppercase tracking-wider">Contact Info</h4>
                                <div><span className="text-slate-500">Name:</span> <span className="text-white ml-2">{lead.name || '—'}</span></div>
                                <div><span className="text-slate-500">Email:</span> <span className="text-white ml-2">{lead.email || '—'}</span></div>
                                <div><span className="text-slate-500">Phone:</span> <span className="text-white ml-2">{lead.phone || '—'}</span></div>
                                <div><span className="text-slate-500">Vertical:</span> <span className="text-white ml-2">{lead.vertical || '—'}</span></div>
                                <div><span className="text-slate-500">Payout:</span> <span className="text-emerald-400 ml-2 font-mono">{lead.payout ? `$${Number(lead.payout).toFixed(2)}` : '—'}</span></div>
                              </div>
                              <div className="space-y-2">
                                <h4 className="text-emerald-400 font-semibold text-xs uppercase tracking-wider">Attribution</h4>
                                <div><span className="text-slate-500">Page URL:</span> <span className="text-white ml-2 text-xs break-all">{lead.page_url || '—'}</span></div>
                                <div><span className="text-slate-500">Referrer:</span> <span className="text-white ml-2 text-xs break-all">{lead.referrer || '—'}</span></div>
                                <div><span className="text-slate-500">UTM Source:</span> <span className="text-white ml-2">{lead.utm_source || '—'}</span></div>
                                <div><span className="text-slate-500">UTM Medium:</span> <span className="text-white ml-2">{lead.utm_medium || '—'}</span></div>
                                <div><span className="text-slate-500">UTM Campaign:</span> <span className="text-white ml-2">{lead.utm_campaign || '—'}</span></div>
                                <div><span className="text-slate-500">IP:</span> <span className="text-white ml-2">{lead.ip_address || '—'}</span></div>
                              </div>
                              {lead.data && Object.keys(lead.data).length > 0 && (
                                <div className="md:col-span-2 space-y-2">
                                  <h4 className="text-emerald-400 font-semibold text-xs uppercase tracking-wider">All Answers</h4>
                                  <div className="bg-slate-900 rounded-lg p-3 grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                                    {Object.entries(lead.data).map(([key, val]: [string, any]) => (
                                      <div key={key}>
                                        <span className="text-slate-500">{key}:</span>
                                        <span className="text-white ml-1">{String(val)}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    )}
    </>
  );
}
